/**
 * Blog like / dislike server functions.
 *
 * The underlying SQL helpers are SECURITY DEFINER, so their EXECUTE grant was
 * revoked from anon and authenticated: no browser can call them directly any
 * more. Reader feedback flows through these validated server functions, which
 * load the privileged client inside the handler and only ever touch the
 * reaction counters for a single post.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const countsSchema = z.object({
  postId: z.string().uuid(),
  voter: z.string().trim().min(8).max(64),
});

const voteSchema = countsSchema.extend({
  value: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
});

export interface ReactionCounts {
  likes: number;
  dislikes: number;
  mine: number;
}

function normalise(data: unknown): ReactionCounts {
  const r = (data ?? {}) as { likes?: number; dislikes?: number; mine?: number };
  return {
    likes: Number(r.likes ?? 0),
    dislikes: Number(r.dislikes ?? 0),
    mine: Number(r.mine ?? 0),
  };
}

export const getBlogReactions = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => countsSchema.parse(input))
  .handler(async ({ data }): Promise<ReactionCounts> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin.rpc("blog_reaction_counts", {
      _post: data.postId,
      _voter: data.voter,
    });
    if (error) throw new Error("Could not load reactions.");
    return normalise(row);
  });

export const reactToBlogPost = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => voteSchema.parse(input))
  .handler(async ({ data }): Promise<ReactionCounts> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin.rpc("blog_react", {
      _post: data.postId,
      _voter: data.voter,
      _value: data.value,
    });
    if (error) throw new Error("Could not record your reaction.");
    return normalise(row);
  });
